import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { cn } from '@utils/cn';
import { htmlToPlainText } from '@utils/richText';
import { Calendar, Clock, DollarSign, Package, Trash2, Wrench, ChevronDown, ChevronRight } from 'lucide-react';
import { InlineEditableValue } from './InlineEditableValue';
import { AnimatedPlanRow } from '@utils/projectPlanDraft.utils';
import { useTranslation } from '@hooks/useTranslation';

export interface TaskTreeEditPatch {
  name?: string;
  duration?: number;
  /** Start offset in working days from the project start. */
  daysFromStart?: number;
  manHours?: number;
  budget?: number;
}

interface TaskTreeProps {
  rows: AnimatedPlanRow[];
  editable?: boolean;
  animated?: boolean;
  /** Sprint 36, story-192 (D2): budget renders only when the caller's role sees cost-side money. Default true. */
  showBudget?: boolean;
  onEditTask?: (tempId: string, patch: TaskTreeEditPatch) => void;
  /** Deleting a phase deletes its subtasks; the caller removes the whole subtree. */
  onDeleteTask?: (tempId: string) => void;
}

/**
 * Sprint 31, story-161. Recursive-in-spirit (two-level, matching the pre-existing
 * `TemplateDetailModal` tree this was generalized from), read-only/editable/animated task list.
 * Consumes pre-flattened `AnimatedPlanRow[]` (see `projectPlanDraft.utils.ts`) rather than
 * recomputing tree/diff state itself, so the same component serves both the plain template
 * detail view and the AI review screen.
 */
export const TaskTree: React.FC<TaskTreeProps> = ({ rows, editable, animated, showBudget = true, onEditTask, onDeleteTask }) => {
  const t = useTranslation('taskTree');
  const prefersReducedMotion = useReducedMotion();
  const reduceMotion = !animated || prefersReducedMotion;
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const toggle = (tempId: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(tempId)) next.delete(tempId);
      else next.add(tempId);
      return next;
    });
  };

  const phaseIds = new Set(rows.filter((r) => r.depth === 0).map((r) => r.tempId));

  return (
    <div className="space-y-2" data-testid="task-tree">
      <AnimatePresence initial={false}>
        {rows.map((row) => {
          if (row.depth === 1 && collapsed.has(row.task.parentTempId || '')) return null;
          const hasChildren = row.depth === 0 && rows.some((r) => r.depth === 1 && r.task.parentTempId === row.tempId);
          const isCollapsed = collapsed.has(row.tempId);
          const materials = row.task.warehouseMaterials || [];
          const equipments = row.task.warehouseEquipments || [];
          const highlightRing = row.animState === 'added';
          const subtaskCount = row.depth === 0 ? rows.filter((r) => r.depth === 1 && r.task.parentTempId === row.tempId).length : 0;
          // Top-level rows are what the UI calls phases (walkthrough fix, sprint 38).
          const deleteLabel = row.depth === 0 ? t.deletePhase : t.deleteTask;

          return (
            <motion.div
              key={row.tempId}
              layout={!reduceMotion}
              initial={reduceMotion ? false : { opacity: 0, x: row.depth === 0 ? -8 : 8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={reduceMotion ? { opacity: 0 } : { opacity: 0, height: 0 }}
              transition={reduceMotion ? { duration: 0 } : { duration: 0.2, delay: row.delayMs / 1000 }}
              className={cn(row.depth === 0 ? 'ml-0' : 'ml-6 mt-1')}
              data-testid={`task-tree-row-${row.tempId}`}
              data-anim-state={row.animState}>
              <div
                className={cn(
                  'flex items-start gap-2 p-3 rounded-lg transition-colors',
                  row.depth === 0
                    ? 'bg-gray-50 dark:bg-gray-700/50'
                    : 'bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700',
                  // Inset so the scroll container never clips it (walkthrough fix, sprint 38).
                  highlightRing && 'ring-2 ring-inset ring-amber-400',
                  row.animState === 'updated' && 'bg-amber-50 dark:bg-amber-900/20',
                )}>
                {phaseIds.has(row.tempId) && (
                  <button
                    type="button"
                    onClick={() => hasChildren && toggle(row.tempId)}
                    className={cn(
                      'mt-0.5 p-0.5 rounded flex-shrink-0',
                      hasChildren ? 'text-gray-500 cursor-pointer' : 'text-transparent',
                    )}>
                    {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <InlineEditableValue
                      kind="text"
                      value={row.task.name}
                      disabled={!editable}
                      ariaLabel={t.editName}
                      testId={`task-tree-name-${row.tempId}`}
                      display={<span className="text-sm font-medium text-gray-900 dark:text-white break-words text-left">{row.task.name}</span>}
                      className="min-w-0 flex-1 text-left"
                      onCommit={(next) => onEditTask?.(row.tempId, { name: next as string })}
                    />
                    <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 uppercase flex-shrink-0">
                      {row.task.taskType}
                    </span>
                  </div>

                  {row.task.description && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-2 whitespace-pre-line">
                      {htmlToPlainText(row.task.description)}
                    </p>
                  )}

                  <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
                    {((row.task.duration != null && row.task.duration > 0) || editable) && (
                      <span className={cn('flex items-center gap-1', row.changedFields.has('duration') && 'text-amber-600 font-medium')}>
                        <Calendar className="w-3 h-3" />
                        <InlineEditableValue
                          kind="days"
                          value={row.task.duration ?? 0}
                          disabled={!editable}
                          ariaLabel={t.editDuration}
                          testId={`task-tree-duration-${row.tempId}`}
                          display={
                            row.task.duration === 1
                              ? t.dayOne.replace('{n}', '1')
                              : t.dayOther.replace('{n}', String(row.task.duration ?? 0))
                          }
                          onCommit={(next) => onEditTask?.(row.tempId, { duration: next as number })}
                        />
                      </span>
                    )}
                    {(row.task.daysFromStart != null || editable) && (
                      <span className={cn('flex items-center gap-1', row.changedFields.has('daysFromStart') && 'text-amber-600 font-medium')}>
                        <InlineEditableValue
                          kind="offset"
                          value={row.task.daysFromStart ?? 0}
                          disabled={!editable}
                          ariaLabel={t.editStart}
                          testId={`task-tree-start-${row.tempId}`}
                          display={t.dayN.replace('{n}', String(row.task.daysFromStart ?? 0))}
                          onCommit={(next) => onEditTask?.(row.tempId, { daysFromStart: next as number })}
                        />
                      </span>
                    )}
                    {row.task.manHours != null && row.task.manHours > 0 && (
                      <span className={cn('flex items-center gap-1', row.changedFields.has('manHours') && 'text-amber-600 font-medium')}>
                        <Clock className="w-3 h-3" />
                        {row.task.manHours}h
                      </span>
                    )}
                    {showBudget && row.task.budget != null && row.task.budget > 0 && (
                      <span className={cn('flex items-center gap-1', row.changedFields.has('budget') && 'text-amber-600 font-medium')}>
                        <DollarSign className="w-3 h-3" />
                        ${row.task.budget.toLocaleString()}
                      </span>
                    )}
                    {materials.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Package className="w-3 h-3" />
                        {materials.length === 1
                          ? t.materialOne.replace('{n}', '1')
                          : t.materialOther.replace('{n}', String(materials.length))}
                      </span>
                    )}
                    {equipments.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Wrench className="w-3 h-3" />
                        {equipments.length === 1
                          ? t.equipmentOne.replace('{n}', '1')
                          : t.equipmentOther.replace('{n}', String(equipments.length))}
                      </span>
                    )}
                  </div>

                  {materials.length > 0 && (
                    <div className="mt-2 pl-2 border-l-2 border-orange-200 dark:border-orange-800">
                      {materials.map((mat, idx) => (
                        <div key={idx} className="text-xs text-gray-500 dark:text-gray-400 py-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 min-w-0 break-words">
                          <Package className="w-2.5 h-2.5 text-orange-400" />
                          <span>{mat.name}</span>
                          {mat.quantity != null && (
                            <span className="text-gray-400 dark:text-gray-500">
                              x{mat.quantity} {mat.unit || ''}
                            </span>
                          )}
                          <span
                            className={cn(
                              'px-1 py-0.5 rounded text-[9px] uppercase',
                              mat.source === 'WAREHOUSE'
                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                                : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
                            )}>
                            {mat.source === 'WAREHOUSE' ? t.catalogChip : t.manualChip}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {equipments.length > 0 && (
                    <div className="mt-2 pl-2 border-l-2 border-purple-200 dark:border-purple-800">
                      {equipments.map((eq, idx) => (
                        <div key={idx} className="text-xs text-gray-500 dark:text-gray-400 py-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 min-w-0 break-words">
                          <Wrench className="w-2.5 h-2.5 text-purple-400" />
                          <span>{eq.name}</span>
                          {eq.estimatedHours != null && <span className="text-gray-400 dark:text-gray-500">{eq.estimatedHours}h</span>}
                          <span
                            className={cn(
                              'px-1 py-0.5 rounded text-[9px] uppercase',
                              eq.source === 'WAREHOUSE'
                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                                : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
                            )}>
                            {eq.source === 'WAREHOUSE' ? t.catalogChip : t.manualChip}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {editable && onDeleteTask && (
                  <div className="flex-shrink-0">
                    {confirmDeleteId === row.tempId ? (
                      <div className="flex flex-col items-end gap-1 text-xs" role="alertdialog" data-testid={`task-tree-delete-confirm-${row.tempId}`}>
                        <span className="text-gray-700 dark:text-gray-300 text-right">
                          {row.depth === 0 && subtaskCount > 0
                            ? t.deleteConfirmPhase.replace('{name}', row.task.name).replace('{n}', String(subtaskCount))
                            : t.deleteConfirmTask.replace('{name}', row.task.name)}
                        </span>
                        <span className="flex gap-2">
                          <button
                            type="button"
                            data-testid={`task-tree-delete-confirm-yes-${row.tempId}`}
                            onClick={() => {
                              setConfirmDeleteId(null);
                              onDeleteTask(row.tempId);
                            }}
                            className="px-2 py-1 rounded bg-red-600 text-white">
                            {deleteLabel}
                          </button>
                          <button type="button" onClick={() => setConfirmDeleteId(null)} className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600">
                            {t.cancel}
                          </button>
                        </span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        aria-label={deleteLabel}
                        data-testid={`task-tree-delete-${row.tempId}`}
                        onClick={() => setConfirmDeleteId(row.tempId)}
                        className="p-1 rounded text-gray-400 hover:text-red-600 focus:text-red-600">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

export default TaskTree;
